/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 루트 블록(최상위 콘텐츠 영역을 담는 컨테이너)을 정의한다.
 *
 * @file /src/rootin/blocks/root/Root.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 26.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Container } from '@rootin/elements/container/Container';
import { Editor } from '@rootin/core/Editor';
import { Dom } from '@utils/Dom';
import { LanguageText } from '@utils/Language';
import './Root.css';

export class Root extends Block {
    /**
     * 블록타입
     */
    static type: string = 'root';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string = '';

    /**
     * 블록명
     */
    static label: LanguageText = '';

    /**
     * 컨테이너
     */
    body: Container;

    /**
     * 루트 블록(최상위 콘텐츠 영역을 담는 컨테이너)을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     * @param blocks - 본문에 허용하는 블록타입 목록
     */
    constructor(editor: Editor, blocks: string[]) {
        super(editor);
        this.body = new Container(this, { name: 'body', blocks });
        this.$getBlock().append(this.body.$getElement());
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.body.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return false;
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), body: this.body.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.body.setValue(value.body);
    }
}
